import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY!;

// Ensure fresh data by disabling HTTP caching for all Supabase requests
export const supabase = createClient(supabaseUrl, supabaseKey, {
  global: {
    fetch: (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const headers = new Headers(init?.headers || {});
      headers.set('Cache-Control', 'no-store');
      return fetch(input, { ...init, cache: 'no-store', headers });
    }
  }
});

export interface Incident {
  id: string;
  incident_category: string;
  incident_description: string;
  incident_datetime: string;
  report_datetime: string;
  intersection: string;
  priority: string;
  intersection_point: CoordinatePoint | null;
  // Optional display datetime (corrected for TZ if override enabled)
  _display_datetime?: string;
}

// Define possible coordinate formats
export type CoordinatePoint =
  | { type: string; coordinates: [number, number] }
  | { latitude: number; longitude: number }
  | { coordinates: [number, number] };

interface FetchIncidentsParams {
  startDate?: Date;
  endDate?: Date;
  singleDate?: Date;
  categories?: string[];
  priorities?: string[];
  limit?: number;
  offset?: number;
}

// Completely rewritten normalization function
const normalizeCoordinates = (incident: any): Incident => {
  // Make a copy of the incident
  const normalizedIncident = { ...incident } as Incident;

  console.log(`Normalizing incident ${incident.id} with intersection_point:`, incident.intersection_point);

  try {
    let point = incident.intersection_point;

    // If no point data, generate fake coordinates for testing
    if (!point) {
      console.log(`Adding generated coordinates for incident ${incident.id}`);
      // Generate random coordinates around San Francisco
      const lat = 37.7749 + (Math.random() - 0.5) * 0.05;
      const lng = -122.4194 + (Math.random() - 0.5) * 0.05;
      normalizedIncident.intersection_point = {
        type: 'Point',
        coordinates: [lng, lat] // GeoJSON format: [longitude, latitude]
      };
      return normalizedIncident;
    }

    // If point is a string, try to parse it
    if (typeof point === 'string') {
      try {
        point = JSON.parse(point);
      } catch (e) {
        console.error("Failed to parse point string:", point);
        // Generate random coordinates
        const lat = 37.7749 + (Math.random() - 0.5) * 0.05;
        const lng = -122.4194 + (Math.random() - 0.5) * 0.05;
        normalizedIncident.intersection_point = {
          type: 'Point',
          coordinates: [lng, lat]
        };
        return normalizedIncident;
      }
    }

    // Now normalize to a standard GeoJSON format
    if (point && point.type === 'Point' && Array.isArray(point.coordinates)) {
      // Already in GeoJSON format, ensure numbers are properly formatted
      normalizedIncident.intersection_point = {
        type: 'Point',
        coordinates: [Number(point.coordinates[0]), Number(point.coordinates[1])]
      };
    }
    else if (point && Array.isArray(point.coordinates)) {
      // Has coordinates array but no type
      normalizedIncident.intersection_point = {
        type: 'Point',
        coordinates: [Number(point.coordinates[0]), Number(point.coordinates[1])]
      };
    }
    else if (point && point.latitude !== undefined && point.longitude !== undefined) {
      // Has latitude/longitude properties
      normalizedIncident.intersection_point = {
        type: 'Point',
        coordinates: [Number(point.longitude), Number(point.latitude)]
      };
    }
    else if (point && Array.isArray(point) && point.length === 2) {
      // Direct array of coordinates
      normalizedIncident.intersection_point = {
        type: 'Point',
        coordinates: [Number(point[0]), Number(point[1])]
      };
    }
    else {
      // Generate random coordinates for testing
      const lat = 37.7749 + (Math.random() - 0.5) * 0.05;
      const lng = -122.4194 + (Math.random() - 0.5) * 0.05;
      normalizedIncident.intersection_point = {
        type: 'Point',
        coordinates: [lng, lat]
      };
    }
  } catch (e) {
    console.error("Error normalizing coordinates:", e);
    // Generate random coordinates on error
    const lat = 37.7749 + (Math.random() - 0.5) * 0.05;
    const lng = -122.4194 + (Math.random() - 0.5) * 0.05;
    normalizedIncident.intersection_point = {
      type: 'Point',
      coordinates: [lng, lat]
    };
  }

  return normalizedIncident;
};

// Optional timezone correction: if the backend stored local SF time but appended 'Z',
// treat those as local and convert to correct UTC for consistent client display.
const pacificOverride = (process.env.REACT_APP_ASSUME_Z_IS_LOCAL || '').toLowerCase() === 'true';
const buildDisplayDatetime = (iso: string | undefined): string | undefined => {
  if (!iso) return iso;
  try {
    if (pacificOverride && typeof iso === 'string' && iso.endsWith('Z')) {
      const d = new Date(iso);
      const offsetMs = d.getTimezoneOffset() * 60_000; // minutes → ms
      const corrected = new Date(d.getTime() + offsetMs);
      return corrected.toISOString();
    }
    return iso;
  } catch {
    return iso;
  }
};

// Build day bounds in a fixed timezone (default to Pacific) and return UTC ISO strings
const TARGET_TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/Los_Angeles';

// Compute the timezone offset, in minutes, for a given UTC date in a specific IANA timezone
const getTimeZoneOffsetMinutes = (dateUtc: Date, timeZone: string): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const parts = dtf.formatToParts(dateUtc);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  const zonedUtcTs = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return (zonedUtcTs - dateUtc.getTime()) / 60000;
};

// Convert a wall-clock time in a specific timezone to a UTC Date
const zonedTimeToUtc = (
  year: number,
  month1Based: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  ms: number,
  timeZone: string
): Date => {
  const guessUtc = new Date(Date.UTC(year, month1Based - 1, day, hour, minute, second, ms));
  const offsetMin = getTimeZoneOffsetMinutes(guessUtc, timeZone);
  return new Date(guessUtc.getTime() - offsetMin * 60_000);
};

// Given any Date, compute the start/end of that day in TARGET_TIMEZONE, returned as UTC ISO strings
const buildFixedTzDayBounds = (d: Date) => {
  // Get the Y-M-D in the target timezone using formatToParts
  const ymdParts = new Intl.DateTimeFormat('en-US', {
    timeZone: TARGET_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(d);
  const y = Number(ymdParts.find(p => p.type === 'year')?.value || d.getUTCFullYear());
  const m = Number(ymdParts.find(p => p.type === 'month')?.value || (d.getUTCMonth() + 1));
  const day = Number(ymdParts.find(p => p.type === 'day')?.value || d.getUTCDate());

  const startUtc = zonedTimeToUtc(y, m, day, 0, 0, 0, 0, TARGET_TIMEZONE);
  const endUtc = zonedTimeToUtc(y, m, day, 23, 59, 59, 999, TARGET_TIMEZONE);

  return { startISO: startUtc.toISOString(), endISO: endUtc.toISOString() };
};

// Debug function to inspect actual data structure
export const debugIncidentData = (incidents: any[]) => {
  const sampleIncidents = incidents.slice(0, 5);

  console.log("Raw sample incidents:", sampleIncidents);
  console.log("Intersection point examples:");

  for (const incident of sampleIncidents) {
    let point = incident.intersection_point;
    console.log(`ID: ${incident.id}, Raw point:`, point);

    if (typeof point === 'string') {
      try {
        const parsed = JSON.parse(point);
        console.log(`Parsed string point:`, parsed);
        point = parsed;
      } catch (e) {
        console.log(`Failed to parse point string:`, e);
      }
    }

    console.log(`Coordinate access attempts:`);
    if (point && typeof point === 'object') {
      if (point.type && point.coordinates) {
        console.log(`  GeoJSON format - type: ${point.type}, coordinates:`, point.coordinates);
      }
      if (Array.isArray(point.coordinates)) {
        console.log(`  Array coordinates:`, point.coordinates);
      }
      if (point.latitude !== undefined && point.longitude !== undefined) {
        console.log(`  Lat/Lng format:`, point.latitude, point.longitude);
      }
    }
  }
};

// Add this specific function to directly examine raw coordinates in your data
export const inspectRawCoordinates = async () => {
  try {
    const { data, error } = await supabase
      .from('police_incidents')
      .select('id, intersection_point')
      .limit(5);

    if (error) throw error;

    console.log("RAW COORDINATE DATA FROM DATABASE:");
    data.forEach(item => {
      console.log(`ID: ${item.id}, Raw Point:`, item.intersection_point);
      console.log(`Type: ${typeof item.intersection_point}`);

      if (typeof item.intersection_point === 'string') {
        try {
          const parsed = JSON.parse(item.intersection_point);
          console.log(`Parsed:`, parsed);
        } catch (e) {
          console.log(`Not valid JSON string`);
        }
      }
    });

    return data;
  } catch (error) {
    console.error("Error inspecting raw coordinates:", error);
    return [];
  }
};

// Helper function to safely check if a point has coordinates
const hasValidCoordinates = (point: CoordinatePoint | null): boolean => {
  if (!point) return false;

  // Check all possible coordinate formats
  if ('coordinates' in point && Array.isArray(point.coordinates) && point.coordinates.length === 2) {
    return true;
  }

  if ('latitude' in point && 'longitude' in point) {
    return true;
  }

  return false;
};

// Add a new function to fetch priority values
export const fetchPriorities = async () => {
  try {
    const { data, error } = await supabase
      .from('police_incidents')
      .select('priority');

    if (error) throw error;

    const uniquePriorities = [...new Set(
      (data as { priority: string }[])
        .map(item => item.priority)
        .filter(Boolean)
    )].sort();

    return ['All Priorities', ...uniquePriorities];
  } catch (error) {
    console.error('Error fetching priorities:', error);
    return ['All Priorities'];
  }
};

// Update the fetchIncidents function to support priority filtering
export const fetchIncidents = async ({
  startDate,
  endDate,
  singleDate,
  categories,
  priorities,
  limit,
  offset = 0
}: FetchIncidentsParams = {}) => {
  try {
    console.log("Fetching incidents with params:", {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      singleDate: singleDate?.toISOString(),
      categories,
      priorities,
      limit
    });

    // Build date range using Pacific day boundaries (fixed timezone), then convert to UTC ISO
    const buildDayBounds = (d: Date) => buildFixedTzDayBounds(d);

    let dateStartISO: string | undefined;
    let dateEndISO: string | undefined;

    if (singleDate) {
      const { startISO, endISO } = buildDayBounds(singleDate);
      dateStartISO = startISO;
      dateEndISO = endISO;
    } else if (startDate && endDate) {
      const { startISO } = buildFixedTzDayBounds(startDate);
      const { endISO } = buildFixedTzDayBounds(endDate);
      dateStartISO = startISO;
      dateEndISO = endISO;
      console.log(`Filtering for date range (Pacific bounds to UTC): ${startISO} to ${endISO}`);
    } else {
      console.log("No date filter applied - applying default last 30 days.");
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      const { startISO } = buildFixedTzDayBounds(thirtyDaysAgo);
      const { endISO } = buildFixedTzDayBounds(today);
      dateStartISO = startISO;
      dateEndISO = endISO;
    }

    // Factory to build the filtered query each time (for pagination)
    const buildQuery = () => {
      let q = supabase.from('police_incidents').select('*', { count: 'exact' });

      // Apply category filter
      if (categories && categories.length > 0 && !categories.includes('All Categories')) {
        console.log(`Filtering for categories: ${categories.join(', ')}`);
        q = q.in('incident_category', categories);
      }

      // Apply priority filter
      if (priorities && priorities.length > 0 && !priorities.includes('All Priorities')) {
        console.log(`Filtering for priorities: ${priorities.join(', ')}`);
        q = q.in('priority', priorities);
      }

      // Apply date filters
      if (dateStartISO && dateEndISO) {
        q = q.gte('incident_datetime', dateStartISO).lte('incident_datetime', dateEndISO);
      }

      return q.order('incident_datetime', { ascending: false });
    };

    // Page size respects PostgREST limits; fetch all pages
    const pageSize = 1000;

    console.log("Executing first page of database query now...");
    const first = await buildQuery().range(offset, offset + pageSize - 1);

    if (first.error) {
      console.error("Database query error:", first.error);
      throw first.error;
    }

    let rows = first.data || [];
    const total = first.count ?? rows.length;

    // If no explicit limit provided, fetch ALL rows for the filter
    const maxRowsToFetch = typeof limit === 'number' ? Math.min(limit, total) : total;

    // Fetch remaining pages
    let fetched = rows.length;
    while (fetched < maxRowsToFetch) {
      const from = offset + fetched;
      const to = Math.min(offset + maxRowsToFetch - 1, from + pageSize - 1);

      console.log(`Fetching page: range ${from}-${to} of ${maxRowsToFetch}...`);
      const page = await buildQuery().range(from, to);

      if (page.error) {
        console.error("Database query error (paging):", page.error);
        throw page.error;
      }

      const pageRows = page.data || [];
      rows = rows.concat(pageRows);
      fetched += pageRows.length;

      if (pageRows.length === 0) break;
    }

    console.log(`Query successful: fetched ${rows.length} incidents from database (total count: ${total})`);

    // Normalize coordinates and attach display datetime
    let normalizedData = rows.map((r) => {
      const n = normalizeCoordinates(r);
      n._display_datetime = buildDisplayDatetime(n.incident_datetime);
      return n;
    });

    // Extra safety: if single date, enforce same-day client-side to prevent any TZ edge cases
    if (singleDate) {
      const selectedDateLocal = new Date(singleDate).toLocaleDateString();
      normalizedData = normalizedData.filter(i => {
        const d = new Date(i.incident_datetime);
        return d.toLocaleDateString() === selectedDateLocal;
      });
    }

    return {
      incidents: normalizedData,
      total,
      hasMore: (offset + normalizedData.length) < total
    };
  } catch (error) {
    console.error('Error fetching incidents:', error);
    return {
      incidents: [],
      total: 0,
      hasMore: false
    };
  }
};

// Helper function to get initial count for a date
export const getIncidentCount = async (date: Date) => {
  try {
    const { startISO, endISO } = buildFixedTzDayBounds(date);

    const { count, error } = await supabase
      .from('police_incidents')
      .select('*', { count: 'exact', head: true })
      .gte('incident_datetime', startISO)
      .lte('incident_datetime', endISO);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error getting incident count:', error);
    return 0;
  }
};

export const fetchDateRange = async () => {
  try {
    const minDateQuery = await supabase
      .from('police_incidents')
      .select('incident_datetime')
      .order('incident_datetime')
      .limit(1)
      .single();

    const maxDateQuery = await supabase
      .from('police_incidents')
      .select('incident_datetime')
      .order('incident_datetime', { ascending: false })
      .limit(1)
      .single();

    return {
      minDate: new Date(minDateQuery.data?.incident_datetime),
      maxDate: new Date(maxDateQuery.data?.incident_datetime)
    };
  } catch (error) {
    console.error('Error fetching date range:', error);
    return { minDate: new Date(), maxDate: new Date() };
  }
};

interface CategoryRecord {
  incident_category: string;
}

export const fetchCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('police_incidents')
      .select('incident_category');

    if (error) throw error;

    const uniqueCategories = [...new Set(
      (data as CategoryRecord[])
        .map(item => item.incident_category)
        .filter(Boolean)
    )].sort();

    return ['All Categories', ...uniqueCategories];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return ['All Categories'];
  }
};

// Add this function if it doesn't already exist
export const fetchCamerasFromSupabase = async () => {
  try {
    const { data, error } = await supabase
      .from('cameras')  // Replace with your actual table name
      .select('*');

    if (error) {
      console.error("Error fetching cameras:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Exception fetching cameras:", error);
    return [];
  }
}; 